import ItemCard from "@affinity/components/ItemCard/ItemCard";
import OrderQuantity from "@affinity/components/OrderQuantity/OrderQuantity";
import OrderStepComplete from "@affinity/components/OrderSteps/OrderStepComplete";
import OrderStepIncomplete from "@affinity/components/OrderSteps/OrderStepIncomplete";
import { CardGridContainer } from "@affinity/components/ProductList/ProductListCardGrid";
import { Section } from "@affinity/components/Section";
import { Wrapper } from "@affinity/components/Wrapper";
import * as styleConstants from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import { AddressLookup } from "@affinity/Widgets/Addresses/AddressLookup";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideHeader,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Button, Radio, RadioGroup, Typography } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import styled from "styled-components";

// Page State for Components
interface State {
    /* Quantity Slider */
    outputOrderQuantity?: Number;
    /* TODO */
}

// Styled Components
export const StyledList = styled.ul<InjectableCss>`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${styleHelpers.rem(15)};
    @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
        gap: ${styleHelpers.rem(20)};
    }
`;

export const StyledFigureSteps = styled.figure`
    margin: 0;
    background-color: ${styleHelpers.transparentize(styleConstants.colours.white, 0.5)};
    padding: ${styleHelpers.rem(20)};
    border-radius: ${styleHelpers.rem(10)};
    @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
        padding: ${styleHelpers.rem(30)};
        border-radius: ${styleHelpers.rem(15)};
    }
`;

type OrderCardProps = { badge: string; title: string; total: number; pricePerLitre: number };

const OrderCard: React.FC<OrderCardProps> = ({ badge, title, total, pricePerLitre }) => {
    return (
        <ItemCard.Container>
            <ItemCard.Badge>{badge}</ItemCard.Badge>
            <ItemCard.Title>{title}</ItemCard.Title>
            <ItemCard.Text>
                Total <strong>£{total.toLocaleString()}</strong> inc VAT{" "}
                <div>
                    <small>(£{pricePerLitre.toLocaleString()}/litre ex VAT)</small>
                </div>
            </ItemCard.Text>
            <Button variant="secondary" sizeVariant="medium">
                Select
            </Button>
        </ItemCard.Container>
    );
};

const ProductCards = () => {
    const [value, setValue] = React.useState("regular");

    return (
        <RadioGroup value={value} onChangeHandler={e => setValue(e.target.value)}>
            <CardGridContainer>
                <ItemCard.Container>
                    <Radio value="regular">
                        <ItemCard.Title>Regular Kerosene</ItemCard.Title>
                    </Radio>
                </ItemCard.Container>
                <ItemCard.Container>
                    <Radio value="premium">
                        <ItemCard.Title>Premium Kerosene</ItemCard.Title>
                    </Radio>
                </ItemCard.Container>
            </CardGridContainer>
        </RadioGroup>
    );
};

class FunctionalComponentsPage extends React.Component<PageProps, State> {
    constructor(props: PageProps) {
        super(props);

        this.state = {};
    }

    quantitySliderOnClick = (i: Number) => {
        this.setState({ outputOrderQuantity: i });
    };

    noAction = () => {};

    render() {
        return (
            <Page hasCustomBackground>
                <Wrapper>
                    <div>
                        <Typography variant="h1">Functional Components</Typography>
                        <p>
                            Lightweight page with no redux integration. To be used for development, QA and UAT of react
                            components with hard coded inputs.
                        </p>
                    </div>
                    <Typography variant="h2">Vertical Step Components</Typography>
                    <Section title="Address search">
                        <AddressLookup onSubmit={() => {}} />
                    </Section>
                    <Section title="Price Delivery Card Component">
                        <CardGridContainer>
                            <OrderCard
                                badge="Cheapest deal"
                                title="Delivery 8-10 days"
                                total={510}
                                pricePerLitre={0.4}
                            />
                            <OrderCard
                                badge="Get it sooner"
                                title="Delivery 5-7 days"
                                total={550}
                                pricePerLitre={0.43}
                            />
                        </CardGridContainer>
                    </Section>
                    <Section title="Product Card Component">
                        <ProductCards />
                    </Section>
                    <StyledFigureSteps>
                        <VisuallyHidden as="figcaption">Completed steps:</VisuallyHidden>
                        <StyledList>
                            <OrderStepComplete
                                text1="Fuel type: "
                                text2="Regular"
                                onClick={
                                    this.noAction
                                } /* Dynamically built function with value in function for Fuel MVP, See FuelStepsCompleted */
                                key="Fuel type: "
                            />
                            <OrderStepComplete
                                text1="Quantity: "
                                text2="1,000 litres"
                                onClick={
                                    this.noAction
                                } /* Dynamically built function with value in function for Fuel MVP, See FuelStepsCompleted */
                                key="Quantity: "
                            />
                            <OrderStepComplete
                                text1="Delivery address: "
                                text2="Home (7 The Glebe, Wymondham, Norfolk, NR99 9XX)"
                                onClick={
                                    this.noAction
                                } /* Dynamically built function with value in function for Fuel MVP, See FuelStepsCompleted */
                                key="Delivery address: "
                            />
                        </StyledList>
                    </StyledFigureSteps>
                    <Section title="Quantity Slider Component with confirmation button">
                        <OrderQuantity
                            product="Regular"
                            min={500}
                            max={3000}
                            buttonStep={500}
                            sliderStep={50}
                            onClick={this.quantitySliderOnClick}
                            showConfirmButton
                        />
                    </Section>
                    <p>
                        Output:
                        <span id="outputOrderQuantity">{this.state.outputOrderQuantity?.toLocaleString() ?? "0"}</span>
                    </p>
                    <Section title="Quantity Slider Component">
                        <OrderQuantity
                            product="Regular"
                            min={500}
                            max={5000}
                            buttonStep={500}
                            sliderStep={50}
                            onClick={this.quantitySliderOnClick}
                        />
                    </Section>
                    <p>
                        Output:
                        <span id="outputOrderQuantity">{this.state.outputOrderQuantity?.toLocaleString() ?? "0"}</span>
                    </p>
                    <StyledFigureSteps>
                        <VisuallyHidden as="figcaption">Incomplete steps:</VisuallyHidden>
                        <StyledList>
                            <OrderStepIncomplete key="3. Delivery address" text="3. Delivery address" />
                            <OrderStepIncomplete key="4. Price & delivery options" text="4. Price & delivery options" />
                            <OrderStepIncomplete key="5. Delivery instructions" text="5. Delivery instructions" />
                        </StyledList>
                    </StyledFigureSteps>
                </Wrapper>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: FunctionalComponentsPage,
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            HideHeader,
            HideFooter,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};

export default pageModule;
export const FunctionalComponentsContext = "FunctionalComponentsPage";
